<script lang="ts">
export const SUBNAVBAR_HEIGHTS_PX = { full: 50, compact: 42 } as const;
</script>

<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";
import { isNavbarItemActive, type NavbarItem } from "@/components/shared/navbar/useNavbarItems.ts";

const props = defineProps<{
  items: NavbarItem[];
  fullscreen?: boolean;
  compact?: boolean;
}>();

const route = useRoute();

const height = computed(() =>
  props.compact ? SUBNAVBAR_HEIGHTS_PX.compact : SUBNAVBAR_HEIGHTS_PX.full
);

const visibleItems = computed(() => props.items.filter((item) => !item.hidden));

const isActive = (item: NavbarItem) => {
  return isNavbarItemActive(item, route.path);
};
</script>

<template>
  <div
    class="subnav tw:w-full"
    :class="{ 'subnav--compact': compact }"
  >
    <v-container
      :class="[
        'tw:h-full tw:py-0! tw:px-4! tw:md:px-6!',
        fullscreen ? 'tw:max-w-full!' : 'tw:max-w-[1366px]!',
      ]"
    >
      <v-tabs
        align-tabs="start"
        :height="height"
        show-arrows
        hide-slider
        class="tw:transition-all! tw:duration-500 tw:ease-in-out"
      >
        <v-tab
          v-for="item in visibleItems"
          :key="item.url"
          :active="isActive(item)"
          :data-tour="item.dataTour"
          :disabled="item.disabled"
          :ripple="false"
          :to="item.to ?? item.url"
          class="subnav-tab tw:min-w-0! tw:opacity-100!"
          :class="{ 'subnav-tab--active': isActive(item), 'tw:ml-6!': item.dividerBefore }"
          :style="{ '--flow-color': item.accentColor ?? 'rgb(var(--v-theme-secondary))' }"
        >
          <span
            v-if="item.dividerBefore"
            aria-hidden="true"
            class="tw:absolute tw:top-1/2 tw:left-[-12px] tw:h-5 tw:w-px tw:-translate-y-1/2 tw:bg-on-surface/10"
          />

          <!-- Sub-tabs stay text-only here; `item.icon` is carried for the mobile drawer, where
               these entries need an icon to sit alongside the top-level nav items. -->
          <span class="subnav-tab__inner tw:inline-flex tw:items-center">
            <span>{{ item.title }}</span>
            <v-badge
              v-if="item.badgeNumber && item.badgeNumber > 0"
              inline
              :content="item.badgeNumber > 999 ? '999+' : item.badgeNumber"
              :max="99"
              class="badge"
              :style="{ '--badge-color': item.badgeColor ?? 'rgb(var(--v-theme-secondary))' }"
            />
          </span>
        </v-tab>
      </v-tabs>
    </v-container>
  </div>
</template>

<style scoped lang="scss">
.v-tab :deep(.v-btn__overlay) {
  display: none;
}

.subnav {
  background: rgb(var(--v-theme-surface-subnav));
  box-shadow: inset 0 -1px 0 rgba(var(--v-theme-on-surface), 0.07);
}

.subnav-tab {
  position: relative;
  height: 30px !important;
  min-height: 30px;
  padding: 0 12px !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  font-size: 12.5px !important;
  font-weight: 640 !important;
  letter-spacing: -0.005em;
  color: rgba(var(--v-theme-on-surface), 0.65) !important;
  overflow: visible;
  transition:
    color 0.16s ease,
    background-color 0.16s ease;

  & + .subnav-tab {
    margin-left: 4px;
  }

  &:hover {
    color: rgb(var(--v-theme-on-surface)) !important;
    background-color: color-mix(in srgb, var(--flow-color) 8%, transparent);
  }

  &.subnav-tab--active {
    color: rgb(var(--v-theme-on-surface)) !important;
    background: color-mix(in srgb, var(--flow-color) 10%, transparent);

    .subnav-tab__inner::after {
      background: var(--flow-color);
    }
  }
}

.subnav-tab :deep(.v-btn__content) {
  height: 100%;
  overflow: visible;
}

.subnav-tab__inner {
  position: relative;
  height: 100%;

  &::after {
    content: "";
    position: absolute;
    right: 0;
    bottom: -10px;
    left: 0;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: transparent;
  }
}

.subnav--compact .subnav-tab__inner::after {
  bottom: -6px;
}

.v-tabs :deep(.v-slide-group__content) {
  align-items: center;
  overflow: visible;
}
.badge {
  :deep(.v-badge__wrapper) {
    margin-inline: 6px 0;
  }

  :deep(.v-badge__badge) {
    min-width: 20px;
    height: 18px;
    padding: 0 7px;
    border-radius: 999px;

    background: var(--badge-color) !important;
    color: #fff !important;

    font-size: 10px;
    font-weight: 700;
    line-height: 18px;
  }
}
</style>
